import React, { Suspense, lazy } from "react";
import { Routes, Route, useLocation, Navigate, BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import { AuthProvider } from "@/contexts/AuthContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { SystemFlagsProvider } from "@/contexts/SystemFlagsContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminBlock from "@/components/AdminBlock";
import AuthCallback from "@/components/AuthCallback";
import Header from "@/components/Header";
import { Toaster } from "@/components/ui/sonner";

import ErrorBoundary from "@/components/ErrorBoundary";
import InstallPrompt from "@/components/InstallPrompt";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import useHeartbeat from "@/hooks/useHeartbeat";

// Eagerly loaded — landing/critical path
import Discovery from "@/pages/Discovery";

// Lazy-loaded — split into chunks for faster initial paint / lower TBT
const Auth = lazy(() => import("@/pages/Auth"));
const TrustPage = lazy(() => import("@/pages/TrustPage"));
const ProviderDetail = lazy(() => import("@/pages/ProviderDetail"));
const BookingHistory = lazy(() => import("@/pages/BookingHistory"));
const BookingDetail = lazy(() => import("@/pages/BookingDetail"));
const Chat = lazy(() => import("@/pages/Chat"));
const Subscription = lazy(() => import("@/pages/Subscription"));
const Profile = lazy(() => import("@/pages/Profile"));
const CustomerPreview = lazy(() => import("@/pages/CustomerPreview"));
const BecomeProvider = lazy(() => import("@/pages/BecomeProvider"));
const GoogleAuthFinish = lazy(() => import("@/pages/GoogleAuthFinish"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const GroupSessions = lazy(() => import("@/pages/GroupSessions"));
const Favorites = lazy(() => import("@/pages/Favorites"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const ProviderTrialPreview = lazy(() => import("@/pages/ProviderTrialPreview"));
const TrialOnboarding = lazy(() => import("@/pages/TrialOnboarding"));
const CompleteProfile = lazy(() => import("@/pages/CompleteProfile"));
const JoinRedirect = lazy(() => import("@/pages/JoinRedirect"));
import ProfileCompletionGuard from "@/components/ProfileCompletionGuard";

function RouteLoader() {
  return (
    <div
      className="flex items-center justify-center min-h-[60vh]"
      data-testid="route-suspense-loader"
      aria-label="Loading page"
    >
      <div className="h-10 w-10 rounded-full border-2 border-stone-200 border-t-[#4A5D4E] animate-spin" />
    </div>
  );
}

function AppShell() {
  const location = useLocation();
  useHeartbeat();
  // Check URL fragment (not query params) for session_id - synchronous during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <Header />
      <main className="min-h-[calc(100vh-4rem)]">
        <Suspense fallback={<RouteLoader />}>
          <ProfileCompletionGuard>
          <Routes>
            <Route path="/" element={<Navigate to="/discovery" replace />} />
            <Route path="/join" element={<JoinRedirect />} />
            <Route path="/apply" element={<JoinRedirect />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/google/finish" element={<GoogleAuthFinish />} />
            <Route path="/auth/forgot" element={<ForgotPassword />} />
            <Route path="/auth/reset" element={<ResetPassword />} />
            <Route path="/onboarding/complete-profile" element={<CompleteProfile />} />
            <Route path="/discovery" element={<Discovery />} />
            <Route path="/preview/provider-trial" element={<ProviderTrialPreview />} />
            {/* /favourites (UK) is an alias of /favorites (US) — both work. */}
            <Route path="/favorites" element={
              <AdminBlock>
                <Favorites />
              </AdminBlock>
            } />
            <Route path="/favourites" element={
              <AdminBlock>
                <Favorites />
              </AdminBlock>
            } />
            <Route path="/group-sessions" element={
              <AdminBlock>
                <GroupSessions />
              </AdminBlock>
            } />
            <Route path="/privacy" element={<LegalPage path="/legal/privacy" title="Privacy Policy" testid="privacy-page" />} />
            <Route path="/safety" element={<LegalPage path="/legal/safety" title="Safety at COMPANIN" testid="safety-page" />} />
            <Route path="/terms" element={<LegalPage path="/legal/terms" title="Terms & Conditions" testid="terms-page" />} />
            <Route path="/trust" element={<TrustPage />} />
            {/* UK city landing pages — long-tail SEO. Static HTML for each is
                generated at build time by scripts/generate-seo-pages.js. */}
            <Route path="/london" element={<CityPage slug="london" />} />
            <Route path="/manchester" element={<CityPage slug="manchester" />} />
            <Route path="/edinburgh" element={<CityPage slug="edinburgh" />} />
            <Route path="/birmingham" element={<CityPage slug="birmingham" />} />
            <Route path="/glasgow" element={<CityPage slug="glasgow" />} />
            <Route path="/providers/:providerId" element={<ErrorBoundary><ProviderDetail /></ErrorBoundary>} />
            <Route
              path="/bookings"
              element={
                <ProtectedRoute blockAdmin>
                  <BookingHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings/:bookingId"
              element={
                <ProtectedRoute blockAdmin>
                  <ErrorBoundary>
                    <BookingDetail />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat/:bookingId"
              element={
                <ProtectedRoute blockAdmin>
                  <Chat />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription"
              element={
                <ProtectedRoute blockAdmin>
                  <Subscription />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute blockAdmin>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/preview"
              element={
                <ProtectedRoute blockAdmin>
                  <CustomerPreview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/become-provider"
              element={
                <ProtectedRoute blockAdmin>
                  <BecomeProvider />
                </ProtectedRoute>
              }
            />
            <Route
              path="/become-provider/trial"
              element={
                <ProtectedRoute blockAdmin>
                  <TrialOnboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/provider/dashboard"
              element={
                <ProtectedRoute blockAdmin>
                  <BecomeProvider />
                </ProtectedRoute>
              }
            />
            <Route
              path="/provider/payouts"
              element={
                <ProtectedRoute blockAdmin>
                  <BecomeProvider />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin">
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/discovery" replace />} />
          </Routes>
          </ProfileCompletionGuard>
        </Suspense>
      </main>
      <Footer />
      <Toaster richColors closeButton />
      <InstallPrompt />
      <CookieConsent />
    </>
  );
}

function App() {
  // Seeding intentionally removed — we used to call POST /api/seed on every
  // boot to populate demo providers, but that bled into production and
  // recreated synthetic profiles on every refresh. Backend now requires an
  // explicit ENABLE_SEED=true env flag, so the call is a no-op anyway.
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <CurrencyProvider>
              <FavoritesProvider>
                <SystemFlagsProvider>
                  <AppShell />
                </SystemFlagsProvider>
              </FavoritesProvider>
            </CurrencyProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
